import axios from "axios";
import type maplibregl from "maplibre-gl";
import { AnimatePresence, motion } from "motion/react";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import { BIKE_STATION_POPUP_CONFIG } from "../../../config/bikeStationPopup.configuration";
import { useOverlayScrollIsolationHook } from "../../../hooks/useOverlayScrollIsolationHook";
import { useReducedMotion } from "../../../hooks/useReducedMotion.hook";
import ArrowRight from "../../../image/icons/map/details/ArrowRight.svg?react";
import NavigationIcon from "../../../image/icons/navigation/Navigation.svg";
import ReturnBackIcon from "../../../image/icons/navigation/ReturnBackIcon.svg?react";
import { convertGeocodeItemToSearchResultItem } from "../../../tools/GeocodeTools";
import type {
    BikeStationRentalUrisDTO,
    BikeStationStatusDTO,
    BikeStationVehicleDTO,
} from "../../../types/dtos/api/bike-station.dto";
import { GeocodeSource, GeocodeType, type SearchResultItem } from "../../../types/geocode-ui.types";
import { resolveBookingUrlFromBookingUrls } from "../../ItinerariesPlanner/itineraryDetails/offerPricing/helpers/OfferPricingUrl.helper";
import { resolveUiEaseOut } from "../../shared/motion/uiMotion.tools";
import BikeStationPopupLoadingSkeleton from "./BikeStationPopupLoadingSkeleton";
import BikeStationPopupModernMotionShell from "./BikeStationPopupModernMotionShell";
import {
    BIKE_POPUP_CHIP_CLASS_NAME,
    BIKE_POPUP_EDGE_MARGIN_DEFAULT,
    BIKE_POPUP_FALLBACK_TOTAL_HEIGHT_PX,
    BIKE_POPUP_HEADER_CLASS_NAME,
    BIKE_POPUP_ICON_BUTTON_CLASS_NAME,
    BIKE_POPUP_MARGIN_POINT,
    BIKE_POPUP_VEHICLE_LIST_MAX_HEIGHT_PX,
    BIKE_POPUP_VEHICLE_ROW_CLASS_NAME,
} from "./bikeStationPopupModern.layout.constants";
import { getBikeStationPopupTheme } from "./bikeStationPopupModern.theme";
import {
    formatFuelPercent,
    formatRange,
    getBatteryIcon,
    getCapacityFillRatio,
    getVehicleTypeIcon,
    getVehicleTypeLabelForCount,
    resolveBikeStationDisplayName,
} from "./bikeStationPopupModern.tools";

export interface BikeStationPopupModernMargins {
    "marginTop"?:    number;
    "marginRight"?:  number;
    "marginBottom"?: number;
    "marginLeft"?:   number;
}

interface BikeStationPopupModernProps {
    "visible":         boolean;
    "x"?:              number;
    "y"?:              number;
    "name":            string;
    "stationId":       string;
    "operator":        string;
    "operatorName"?:   string;
    "rentalUris"?:     BikeStationRentalUrisDTO;
    "lngLat":          [number, number];
    "bikesAvailable"?: number | null;
    "capacity"?:       number | null;
    "map":             maplibregl.Map | null;
    "isDarkUi":        boolean;
    "margins"?:        BikeStationPopupModernMargins;
    "onClose":         () => void;
    "onSetArrival"?:   ( item: SearchResultItem ) => void;
}

const HEADER_OPACITY = 0.75;

/**
 * Returns the header brand color as an rgba string.
 *
 * @param brandAccent Brand hex color.
 * @param opacity Alpha in [0, 1].
 * @returns CSS rgba string.
 */
const getBrandRgba = ( brandAccent: string, opacity: number ): string => {
    const hex = brandAccent.replace( "#", "" );
    const r = Number.parseInt( hex.slice( 0, 2 ), 16 );
    const g = Number.parseInt( hex.slice( 2, 4 ), 16 );
    const b = Number.parseInt( hex.slice( 4, 6 ), 16 );

    return `rgba(${ r }, ${ g }, ${ b }, ${ opacity })`;
};

/**
 * Builds a vertical gradient for the popup header.
 *
 * @param brandAccent Brand hex color.
 * @param bodyBackground Body background color or undefined.
 * @returns CSS linear-gradient string.
 */
const getBikePopupHeaderBackgroundGradient = (
    brandAccent: string,
    bodyBackground: string | undefined,
): string => {
    const topColor = getBrandRgba( brandAccent, HEADER_OPACITY );
    const bottomColor = bodyBackground ?? "transparent";

    return `linear-gradient(to bottom, ${ topColor }, ${ bottomColor })`;
};

export const BikeStationPopupModern: React.FC<BikeStationPopupModernProps> = ( {
    visible,
    x,
    y,
    name,
    stationId,
    operator,
    operatorName,
    rentalUris,
    lngLat,
    bikesAvailable,
    capacity,
    map,
    isDarkUi,
    "margins": marginsProp,
    onClose,
    onSetArrival,
} ) => {
    const popupRef = useRef<HTMLDivElement | null>( null );
    const scrollRef = useRef<HTMLDivElement | null>( null );
    const openedAtRef = useRef( 0 );
    const reducedMotion = useReducedMotion();

    const [ fetchState, setFetchState ] = useState<{
        "loading": boolean;
        "status":  BikeStationStatusDTO | null;
    }>( {
        "loading": false,
        "status":  null,
    } );
    const [ showArrow, setShowArrow ] = useState( true );
    const [ isIconButtonHovered, setIsIconButtonHovered ] = useState( false );
    const [ isBackButtonHovered, setIsBackButtonHovered ] = useState( false );
    const [ isEbikeDetailOpen, setIsEbikeDetailOpen ] = useState( false );

    const displayName = resolveBikeStationDisplayName( name );
    const displayOperatorName = resolveBikeStationDisplayName( operatorName );

    const bodyTransition = resolveUiEaseOut( reducedMotion, 180 );
    const bodyKey = isEbikeDetailOpen ? "ebike-detail" : "summary";

    const marginTop = marginsProp?.marginTop ?? BIKE_POPUP_EDGE_MARGIN_DEFAULT;
    const marginRight = marginsProp?.marginRight ?? BIKE_POPUP_EDGE_MARGIN_DEFAULT;
    const marginBottom = marginsProp?.marginBottom ?? BIKE_POPUP_EDGE_MARGIN_DEFAULT;
    const marginLeft = marginsProp?.marginLeft ?? BIKE_POPUP_EDGE_MARGIN_DEFAULT;

    const rentalUrl = rentalUris != null
        ? resolveBookingUrlFromBookingUrls( rentalUris as Record<string, string> )
        : null;

    useEffect( () => {
        if ( !visible || stationId === "" || operator === "" ) {
            return;
        }

        const fetchStatus = async(): Promise<void> => {
            setFetchState( {
                "loading": true,
                "status":  null,
            } );

            try {
                const response = await axios.get<BikeStationStatusDTO>( `/api/pois/bike-stations/${ encodeURIComponent( operator ) }/${ encodeURIComponent( stationId ) }` );
                setFetchState( {
                    "loading": false,
                    "status":  response.data,
                } );
            } catch {
                setFetchState( {
                    "loading": false,
                    "status":  null,
                } );
            }
        };

        void fetchStatus();
    }, [ visible, stationId, operator ] );

    useEffect( () => {
        if ( visible ) {
            openedAtRef.current = performance.now();
        }
    }, [ visible ] );

    useEffect( () => {
        if ( !visible ) {
            return;
        }

        const handleClickOutside = ( event: MouseEvent | TouchEvent ): void => {
            if ( event.timeStamp - openedAtRef.current < 250 ) {
                return;
            }

            const target = event.target as Node;

            if ( popupRef.current && !popupRef.current.contains( target ) ) {
                onClose();
            }
        };

        document.addEventListener( "click", handleClickOutside );

        return () => {
            document.removeEventListener( "click", handleClickOutside );
        };
    }, [ visible, onClose ] );

    useOverlayScrollIsolationHook( scrollRef, { "isEnabled": visible && !fetchState.loading } );

    useEffect( () => {
        if ( !visible ) {
            return;
        }

        const popupElement = popupRef.current;

        if ( popupElement === null ) {
            return;
        }

        const handleWheel = ( event: WheelEvent ): void => {
            event.stopPropagation();
        };

        popupElement.addEventListener( "wheel", handleWheel );

        return () => {
            popupElement.removeEventListener( "wheel", handleWheel );
        };
    }, [ visible ] );

    /**
     * Positions the popup before paint and keeps it anchored to the map.
     */
    useLayoutEffect( () => {
        if ( !visible ) {
            return;
        }

        let isCancelled = false;
        let measureFrameId: number | undefined;

        const popupElement = popupRef.current;

        if ( popupElement === null ) {
            return;
        }

        const updatePosition = (): void => {
            if ( isCancelled ) {
                return;
            }

            const fixedWidth = BIKE_STATION_POPUP_CONFIG.fixedWidthPx;

            if ( map !== null ) {
                const position = map.project( lngLat );
                const container = map.getContainer();
                const mapWidth = container.offsetWidth;
                const mapHeight = container.offsetHeight;
                const popupHalfWidth = fixedWidth / 2;
                const tipY = position.y - BIKE_POPUP_MARGIN_POINT;
                const measuredHeight = popupElement.offsetHeight > 0
                    ? popupElement.offsetHeight
                    : BIKE_POPUP_FALLBACK_TOTAL_HEIGHT_PX;

                const nextX = Math.max(
                    popupHalfWidth + marginLeft,
                    Math.min( mapWidth - popupHalfWidth - marginRight, position.x ),
                );
                const nextY = Math.max(
                    marginTop + measuredHeight,
                    Math.min( mapHeight - marginBottom, tipY ),
                );

                popupElement.style.left = `${ nextX - popupHalfWidth }px`;
                popupElement.style.top = `${ nextY - measuredHeight }px`;

                const clampedLeft = position.x < popupHalfWidth + marginLeft;
                const clampedRight = position.x > mapWidth - popupHalfWidth - marginRight;
                const clampedTop = tipY < marginTop + measuredHeight;
                const clampedBottom = tipY > mapHeight - marginBottom;
                const isClamped = clampedLeft || clampedRight || clampedTop || clampedBottom;

                setShowArrow( ( previous ) => ( previous === !isClamped ? previous : !isClamped ) );
            } else if ( x !== undefined && y !== undefined ) {
                const clampedX = Math.max( 8, Math.min( x, window.innerWidth - fixedWidth ) );
                const clampedY = Math.max( 8, Math.min( y, window.innerHeight - 200 ) );

                popupElement.style.left = `${ clampedX }px`;
                popupElement.style.top = `${ clampedY }px`;
                setShowArrow( false );
            }

            popupElement.style.visibility = "visible";
        };

        const attachPositioning = (): void => {
            if ( isCancelled ) {
                return;
            }

            popupElement.style.visibility = "hidden";
            updatePosition();

            if ( map !== null ) {
                map.on( "move", updatePosition );
            }

            measureFrameId = window.requestAnimationFrame( () => {
                if ( !isCancelled ) {
                    updatePosition();
                }
            } );
        };

        const resizeObserver = new ResizeObserver( () => {
            if ( !isCancelled ) {
                updatePosition();
            }
        } );

        attachPositioning();
        resizeObserver.observe( popupElement );

        return () => {
            isCancelled = true;

            if ( measureFrameId !== undefined ) {
                window.cancelAnimationFrame( measureFrameId );
            }

            if ( map !== null ) {
                map.off( "move", updatePosition );
            }

            resizeObserver.disconnect();
        };
    }, [
        lngLat,
        map,
        visible,
        x,
        y,
        marginTop,
        marginRight,
        marginBottom,
        marginLeft,
    ] );

    const buildItem = useCallback( (): SearchResultItem => {
        const geocodeItem = {
            "type":     GeocodeType.ADDRESS,
            "id":       `bike-${ lngLat[1] }-${ lngLat[0] }`,
            "result":   displayName,
            "source":   GeocodeSource.PELIAS,
            "location": {
                "coordinates": {
                    "lat": lngLat[1],
                    "lon": lngLat[0],
                },
            },
        };

        return convertGeocodeItemToSearchResultItem( geocodeItem );
    }, [ lngLat, displayName ] );

    const getIconImageStyle = ( size: number, isMonochrome = false ): React.CSSProperties => ( {
        "filter": isDarkUi
            ?  isMonochrome ? "brightness(0) invert(1)" : "invert(1)"
            :  isMonochrome ? "brightness(0)" : undefined,
        "height": `${ size }px`,
        "width":  `${ size }px`,
    } );

    const RangeIcon: React.FC<{ "color"?: string }> = ( { color } ) => (
        <svg
            fill="none"
            height="16"
            style={{ color }}
            viewBox="0 0 16 16"
            width="16"
            xmlns="http://www.w3.org/2000/svg"
        >
            <circle
                cx="3"
                cy="8"
                fill="currentColor"
                r="1.5"
            />
            <circle
                cx="13"
                cy="8"
                fill="currentColor"
                r="1.5"
            />
            <path
                d="M4.5 8h7"
                stroke="currentColor"
                strokeLinecap="round"
                strokeWidth="1.5"
            />
        </svg>
    );

    const theme = getBikeStationPopupTheme( isDarkUi );
    const availability = fetchState.status?.availabilitySummary;
    const totalBikes = availability?.bikes ?? bikesAvailable;
    const fillRatio = getCapacityFillRatio( totalBikes, capacity );

    const ebikeVehicles = ( fetchState.status?.vehicles ?? [] ).
        filter( ( vehicle ) => vehicle.vehicleTypeId === "ebike" && !vehicle.isReserved && !vehicle.isDisabled ).
        sort( ( a, b ) => ( b.currentRangeMeters ?? -1 ) - ( a.currentRangeMeters ?? -1 ) ).
        slice( 0, BIKE_STATION_POPUP_CONFIG.maxVehicleListItems );

    const maxEbikeRangeMeters = ebikeVehicles.reduce<number | undefined>( ( max, vehicle ) => {
        if ( vehicle.currentRangeMeters == null ) {
            return max;
        }

        return max == null ? vehicle.currentRangeMeters : Math.max( max, vehicle.currentRangeMeters );
    }, undefined );

    const maxEbikeRangeLabel = maxEbikeRangeMeters != null
        ? formatRange( maxEbikeRangeMeters )
        : null;

    const renderCapacityBar = (): React.ReactNode => {
        if ( !BIKE_STATION_POPUP_CONFIG.showCapacityBar || fillRatio == null || capacity == null ) {
            return null;
        }

        const bikesCount = totalBikes ?? 0;
        const availabilityLabel = `Disponibilité: ${ bikesCount } / ${ capacity }`;
        const emptyPercent = ( 1 - fillRatio ) * 100;

        return (
            <div
                className="relative h-[1.5rem] w-full rounded-full overflow-hidden my-3"
                style={{ "backgroundColor": theme.capacityBarBg }}
            >
                <div
                    className="absolute left-0 top-0 h-full rounded-full"
                    style={{
                        "backgroundColor": theme.capacityBarFill,
                        "width":           `${ fillRatio * 100 }%`,
                    }}
                />
                <span
                    className="absolute inset-0 z-10 flex items-center justify-center text-xs font-semibold"
                    style={{ "color": theme.primaryText }}
                >
                    {availabilityLabel}
                </span>
                <span
                    aria-hidden="true"
                    className="absolute inset-0 z-20 flex items-center justify-center text-xs font-semibold"
                    style={{
                        "clipPath": `inset(0 ${ emptyPercent }% 0 0)`,
                        "color":    theme.capacityBarText,
                    }}
                >
                    {availabilityLabel}
                </span>
            </div>
        );
    };

    const resolveVehicleRentalUrl = ( vehicle: BikeStationVehicleDTO ): string | null => {
        if ( vehicle.rentalUris == null ) {
            return null;
        }

        return resolveBookingUrlFromBookingUrls( vehicle.rentalUris as Record<string, string> );
    };

    const getBikeUrlForType = ( vehicleTypeId: string ): string | null => {
        const vehicles = fetchState.status?.vehicles ?? [];
        const availableVehicleWithUrl = vehicles.find( ( vehicle ) => vehicle.vehicleTypeId === vehicleTypeId &&
          !vehicle.isReserved &&
          !vehicle.isDisabled &&
          resolveVehicleRentalUrl( vehicle ) != null );

        return availableVehicleWithUrl != null
            ? resolveVehicleRentalUrl( availableVehicleWithUrl )
            : null;
    };

    const openUrl = ( url: string ): void => {
        if ( url !== "" ) {
            window.open( url, "_blank", "noopener,noreferrer" );
        }
    };

    const handleUrlKeyDown = ( keyboardEvent: React.KeyboardEvent<HTMLButtonElement>, url: string ): void => {
        if ( keyboardEvent.key === "Enter" || keyboardEvent.key === " " ) {
            keyboardEvent.preventDefault();
            keyboardEvent.stopPropagation();
            openUrl( url );
        }
    };

    const renderVehicleTypeChips = (): React.ReactNode => {
        if ( fetchState.loading || availability == null ) {
            return null;
        }

        const handleEbikeKeyDown = ( keyboardEvent: React.KeyboardEvent<HTMLButtonElement> ): void => {
            if ( keyboardEvent.key === "Enter" || keyboardEvent.key === " " ) {
                keyboardEvent.preventDefault();
                keyboardEvent.stopPropagation();
                setIsEbikeDetailOpen( true );
            }
        };

        return (
            <div className="flex flex-col gap-1.5">
                {availability.types.map( ( type ) => {
                    const isEbike = type.vehicleTypeId === "ebike";

                    if ( isEbike && type.count > 0 ) {
                        return (
                            <button
                                aria-label="Voir les vélos électriques"
                                className={`${ BIKE_POPUP_CHIP_CLASS_NAME } text-left w-full cursor-pointer hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1`}
                                key={type.vehicleTypeId}
                                onClick={( clickEvent ) => {
                                    clickEvent.stopPropagation();
                                    setIsEbikeDetailOpen( true );
                                }}
                                onKeyDown={handleEbikeKeyDown}
                                style={{
                                    "backgroundColor": theme.chipBackground,
                                    "borderColor":     theme.chipBorder,
                                }}
                                type="button"
                            >
                                <img
                                    alt={type.vehicleTypeId}
                                    src={getVehicleTypeIcon( type.vehicleTypeId )}
                                    style={getIconImageStyle( 24, true )}
                                />
                                <span
                                    className="text-sm font-medium"
                                    style={{ "color": theme.primaryText }}
                                >
                                    {type.count}
                                    {" "}
                                    {getVehicleTypeLabelForCount( type.vehicleTypeId, type.count ).toLowerCase()}
                                </span>
                                <div className="ml-auto flex items-center gap-1.5">
                                    {BIKE_STATION_POPUP_CONFIG.showBatterySummary &&
                                      availability.maxAvailableBattery != null && (
                                        <div
                                            className="flex flex-col items-center gap-0.5"
                                            style={{ "color": theme.secondaryText }}
                                        >
                                            <img
                                                alt="battery"
                                                src={getBatteryIcon( availability.maxAvailableBattery )}
                                                style={getIconImageStyle( 20 )}
                                            />
                                            {maxEbikeRangeLabel != null && (
                                                <span className="text-xs leading-none">
                                                    {maxEbikeRangeLabel}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    <ArrowRight
                                        className="h-3.5 w-2.5 shrink-0"
                                        style={{ "color": theme.secondaryText }}
                                    />
                                </div>
                            </button>
                        );
                    }

                    if ( type.vehicleTypeId === "bike" ) {
                        const bikeUrl = getBikeUrlForType( "bike" );

                        if ( bikeUrl != null ) {
                            return (
                                <button
                                    aria-label="Ouvrir le lien du vélo"
                                    className={`${ BIKE_POPUP_CHIP_CLASS_NAME } text-left w-full cursor-pointer hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1`}
                                    key={type.vehicleTypeId}
                                    onClick={( clickEvent ) => {
                                        clickEvent.stopPropagation();
                                        openUrl( bikeUrl );
                                    }}
                                    onKeyDown={( keyboardEvent ) => {
                                        handleUrlKeyDown( keyboardEvent, bikeUrl );
                                    }}
                                    style={{
                                        "backgroundColor": theme.chipBackground,
                                        "borderColor":     theme.chipBorder,
                                    }}
                                    type="button"
                                >
                                    <img
                                        alt={type.vehicleTypeId}
                                        src={getVehicleTypeIcon( type.vehicleTypeId )}
                                        style={getIconImageStyle( 24, true )}
                                    />
                                    <span
                                        className="text-sm font-medium"
                                        style={{ "color": theme.primaryText }}
                                    >
                                        {type.count}
                                        {" "}
                                        {getVehicleTypeLabelForCount( type.vehicleTypeId, type.count ).toLowerCase()}
                                    </span>
                                    <ArrowRight
                                        className="ml-auto h-3.5 w-2.5 shrink-0"
                                        style={{ "color": theme.secondaryText }}
                                    />
                                </button>
                            );
                        }
                    }

                    return (
                        <div
                            className={BIKE_POPUP_CHIP_CLASS_NAME}
                            key={type.vehicleTypeId}
                            style={{
                                "backgroundColor": theme.chipBackground,
                                "borderColor":     theme.chipBorder,
                            }}
                        >
                            <img
                                alt={type.vehicleTypeId}
                                src={getVehicleTypeIcon( type.vehicleTypeId )}
                                style={getIconImageStyle( 24, true )}
                            />
                            <span
                                className="text-sm font-medium"
                                style={{ "color": theme.primaryText }}
                            >
                                {type.count}
                                {" "}
                                {getVehicleTypeLabelForCount( type.vehicleTypeId, type.count ).toLowerCase()}
                            </span>
                        </div>
                    );
                } )}
            </div>
        );
    };

    const renderEbikeDetailPanel = (): React.ReactNode => {
        if ( fetchState.status == null ) {
            return null;
        }

        if ( ebikeVehicles.length === 0 ) {
            return (
                <div
                    className="py-4 text-center text-sm"
                    style={{ "color": theme.secondaryText }}
                >
                    Aucun vélo électrique disponible.
                </div>
            );
        }

        return (
            <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                    <button
                        aria-label="Retour"
                        className={BIKE_POPUP_ICON_BUTTON_CLASS_NAME}
                        onClick={( clickEvent ) => {
                            clickEvent.stopPropagation();
                            setIsEbikeDetailOpen( false );
                        }}
                        onMouseEnter={() => {
                            setIsBackButtonHovered( true );
                        }}
                        onMouseLeave={() => {
                            setIsBackButtonHovered( false );
                        }}
                        style={{
                            "backgroundColor": isBackButtonHovered ? theme.iconButtonHover : theme.iconButtonBackground,
                            "borderColor":     theme.cardBorder,
                        }}
                        type="button"
                    >
                        <ReturnBackIcon
                            className="h-4 w-4 shrink-0"
                            style={{ "color": theme.secondaryText }}
                        />
                    </button>
                    <span
                        className="text-sm font-bold"
                        style={{ "color": theme.primaryText }}
                    >
                        Vélos électriques
                    </span>
                </div>
                <div
                    className="flex w-full min-h-0 flex-col gap-1.5 overflow-y-auto [&::-webkit-scrollbar]:hidden"
                    ref={scrollRef}
                    style={{
                        "maxHeight":       `${ BIKE_POPUP_VEHICLE_LIST_MAX_HEIGHT_PX }px`,
                        "scrollbarWidth":  "none",
                        "msOverflowStyle": "none",
                    }}
                >
                    {ebikeVehicles.map( ( vehicle: BikeStationVehicleDTO, vehicleIndex: number ) => {
                        const vehicleUrl = resolveVehicleRentalUrl( vehicle );
                        const isClickable = vehicleUrl != null && vehicleUrl !== "";

                        const rowContent = (
                            <div className="flex items-center w-full gap-2">
                                <div className="flex flex-col gap-1 flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <img
                                            alt={vehicle.vehicleTypeId}
                                            src={getVehicleTypeIcon( vehicle.vehicleTypeId )}
                                            style={getIconImageStyle( 24, true )}
                                        />
                                        <span
                                            className="text-sm font-medium"
                                            style={{ "color": theme.primaryText }}
                                        >
                                            {`Vélo ${ vehicleIndex + 1 }`}
                                        </span>
                                        {!isClickable && vehicle.isReserved && (
                                            <span
                                                className="ml-auto text-xs"
                                                style={{ "color": theme.mutedText }}
                                            >
                                                Réservé
                                            </span>
                                        )}
                                        {!isClickable && vehicle.isDisabled && (
                                            <span
                                                className="ml-auto text-xs"
                                                style={{ "color": theme.errorText }}
                                            >
                                                Indisponible
                                            </span>
                                        )}
                                    </div>
                                    <div
                                        className="flex items-center gap-3 text-xs"
                                        style={{ "color": theme.secondaryText }}
                                    >
                                        {vehicle.currentRangeMeters != null && (
                                            <span className="flex items-center gap-1">
                                                <RangeIcon color={theme.secondaryText} />
                                                <span
                                                    className="inline-block"
                                                    style={{ "minWidth": "8ch" }}
                                                >
                                                    {formatRange( vehicle.currentRangeMeters )}
                                                </span>
                                            </span>
                                        )}
                                        {vehicle.currentFuelPercent != null && (
                                            <span className="flex items-center gap-1">
                                                <img
                                                    alt="battery"
                                                    src={getBatteryIcon( vehicle.currentFuelPercent )}
                                                    style={getIconImageStyle( 16 )}
                                                />
                                                <span
                                                    className="inline-block"
                                                    style={{ "minWidth": "4.5ch" }}
                                                >
                                                    {formatFuelPercent( vehicle.currentFuelPercent )}
                                                </span>
                                            </span>
                                        )}
                                    </div>
                                </div>
                                {isClickable && (
                                    <ArrowRight
                                        className="h-3.5 w-2.5 shrink-0"
                                        style={{ "color": theme.secondaryText }}
                                    />
                                )}
                            </div>
                        );

                        if ( isClickable ) {
                            return (
                                <button
                                    aria-label="Ouvrir le lien du vélo"
                                    className={`${ BIKE_POPUP_VEHICLE_ROW_CLASS_NAME } text-left w-full cursor-pointer hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1`}
                                    key={vehicle.vehicleId}
                                    onClick={( clickEvent ) => {
                                        clickEvent.stopPropagation();
                                        openUrl( vehicleUrl );
                                    }}
                                    onKeyDown={( keyboardEvent ) => {
                                        handleUrlKeyDown( keyboardEvent, vehicleUrl );
                                    }}
                                    style={{
                                        "backgroundColor": theme.chipBackground,
                                        "borderColor":     theme.chipBorder,
                                    }}
                                    type="button"
                                >
                                    {rowContent}
                                </button>
                            );
                        }

                        return (
                            <div
                                className={BIKE_POPUP_VEHICLE_ROW_CLASS_NAME}
                                key={vehicle.vehicleId}
                                style={{
                                    "backgroundColor": theme.chipBackground,
                                    "borderColor":     theme.chipBorder,
                                }}
                            >
                                {rowContent}
                            </div>
                        );
                    } )}
                </div>
            </div>
        );
    };

    const renderBody = (): React.ReactNode => {
        if ( fetchState.loading ) {
            return (
                <BikeStationPopupLoadingSkeleton
                    bikesAvailable={bikesAvailable}
                    capacity={capacity}
                    theme={theme}
                />
            );
        }

        if ( fetchState.status == null && !fetchState.loading ) {
            return (
                <div
                    className="py-4 text-center text-sm"
                    style={{ "color": theme.secondaryText }}
                >
                    Disponibilité inconnue
                </div>
            );
        }

        const showCapacity =
            BIKE_STATION_POPUP_CONFIG.showCapacityBar && fillRatio != null && capacity != null;

        if ( isEbikeDetailOpen ) {
            return (
                <div className="py-3 px-3">
                    {renderEbikeDetailPanel()}
                </div>
            );
        }

        return (
            <div className="py-3 pt-0 px-3 flex flex-col flex-1">
                {showCapacity
                    ? renderCapacityBar()
                    : (
                        <div className="flex items-center justify-between">
                            <span
                                className="text-2xl font-bold"
                                style={{ "color": theme.primaryText }}
                            >
                                {totalBikes ?? 0}
                            </span>
                            <span
                                className="text-sm"
                                style={{ "color": theme.secondaryText }}
                            >
                                {totalBikes === 0
                                    ? "Aucun vélo disponible"
                                    : totalBikes === 1
                                        ? "vélo disponible"
                                        : "vélos disponibles"}
                            </span>
                        </div>
                    )}
                {BIKE_STATION_POPUP_CONFIG.variant !== "compact" && renderVehicleTypeChips()}
            </div>
        );
    };

    if ( !visible ) {
        return null;
    }

    return (
        <div
            className="absolute pointer-events-auto z-30"
            ref={popupRef}
            style={{ "visibility": "hidden" }}
        >
            <BikeStationPopupModernMotionShell
                presenceKey={stationId}
                visible={visible}
            >
                <div
                    className="rounded-xl shadow-lg overflow-hidden border-2 backdrop-blur-sm flex flex-col"
                    style={{
                        "borderColor": getBrandRgba( theme.brandAccent, HEADER_OPACITY ),
                        "minHeight":   "246px",
                        "width":       `${ BIKE_STATION_POPUP_CONFIG.fixedWidthPx }px`,
                    }}
                >
                    {/* Header */}
                    <div
                        className={BIKE_POPUP_HEADER_CLASS_NAME}
                        style={{
                            "background": getBikePopupHeaderBackgroundGradient(
                                theme.brandAccent,
                                theme.bodyBackground,
                            ),
                            "color": theme.headerText,
                        }}
                    >
                        <div className="truncate min-w-0 flex-1">
                            <span className="block truncate">
                                {displayName}
                            </span>
                            {operator !== "" && (
                                <span
                                    className="block truncate text-[0.65rem] leading-4 font-normal"
                                    style={{ "opacity": 0.65 }}
                                >
                                    {rentalUrl != null
                                        ? (
                                            <span
                                                className="cursor-pointer focus-visible:underline"
                                                onClick={() => {
                                                    window.open( rentalUrl, "_blank", "noopener,noreferrer" );
                                                }}
                                                onKeyDown={( keyboardEvent ) => {
                                                    if ( keyboardEvent.key === "Enter" || keyboardEvent.key === " " ) {
                                                        keyboardEvent.preventDefault();
                                                        window.open( rentalUrl, "_blank", "noopener,noreferrer" );
                                                    }
                                                }}
                                                role="link"
                                                tabIndex={0}
                                            >
                                                {displayOperatorName || operator}
                                            </span>
                                        )
                                        : displayOperatorName || operator}
                                </span>
                            )}
                        </div>
                        <button
                            aria-label="S'y rendre"
                            className={BIKE_POPUP_ICON_BUTTON_CLASS_NAME}
                            onClick={() => {
                                onSetArrival?.( buildItem() );
                                onClose();
                            }}
                            onMouseEnter={() => {
                                setIsIconButtonHovered( true );
                            }}
                            onMouseLeave={() => {
                                setIsIconButtonHovered( false );
                            }}
                            style={{
                                "backgroundColor": isIconButtonHovered ? theme.iconButtonHover : theme.iconButtonBackground,
                                "borderColor":     theme.cardBorder,
                            }}
                            type="button"
                        >
                            <img
                                alt=""
                                aria-hidden="true"
                                className="h-4 w-4 shrink-0"
                                src={NavigationIcon}
                                style={{ "filter": isIconButtonHovered ? "brightness(0) invert(1)" : undefined }}
                            />
                        </button>
                    </div>
                    {/* Body */}
                    <div className="relative flex-1">
                        <AnimatePresence
                            initial={false}
                            mode="sync"
                        >
                            <motion.div
                                animate={{ "opacity": 1 }}
                                className="absolute inset-0 flex flex-col"
                                exit={{ "opacity": 0 }}
                                initial={{ "opacity": 0 }}
                                key={bodyKey}
                                style={{ "backgroundColor": theme.bodyBackground }}
                                transition={bodyTransition}
                            >
                                {renderBody()}
                            </motion.div>
                        </AnimatePresence>
                    </div>

                </div>
                {/* Arrow */}
                {showArrow && (
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full">
                        <div
                            className="relative w-0 h-0 border-l-[10px] border-r-[10px] border-t-[10px] border-l-transparent border-r-transparent"
                            style={{ "borderTopColor": getBrandRgba( theme.brandAccent, HEADER_OPACITY ) }}
                        />
                    </div>
                )}
            </BikeStationPopupModernMotionShell>
        </div>
    );
};
